import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, runInInjectionContext, ViewEncapsulation } from '@angular/core';

import { ClientBO, ClientQLCollectionItem, ClientRepositoryBase, ClientsWithSameNameItem, ClientsWithSamePropertyQuery, ClientsWithSameTinQueryProperties, ContextKeys, CountryCollectionQuery, CountryItem, fillClientBasedOnDocumentClient, NipValidator } from '@skylab-shared/business';
import { L10nBase, LocalContext, RepositoryResponseCode } from '@skylab-shared/core';
import { ClientKind, ClientModel, DocumentBuyerModel, DocumentClientModel, InventoryDocumentKind, PurchaseDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';

import { ClientRepository, clientSetDataFromGus } from '@skylab-angular-shared/business';
import { L10n, QueryBus, RoutingService } from '@skylab-angular-shared/core';
import { BooleanValueAccessorDirective, ButtonComponent, DialogButtonResult, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogResolution, DialogState, FormContainerComponent, ValidationAreaComponent } from '@skylab-angular-shared/ui';

import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { AppStates } from '@skylab-shared/scenes';
import { BehaviorSubject } from 'rxjs';
import { SelectDuplicatedClientDialogComponent, SelectDuplicatedClientDialogResult } from '../select-duplicated-client-dialog/select-duplicated-client-dialog.component';
import { ClientDialogFormComponent } from './ui/client-dialog-form.component';
import { ClientDialogOtherBuyerFormComponent } from './ui/client-dialog-other-buyer-form.component';

type DocumentKind = SaleDocumentKind | PurchaseDocumentKind | InventoryDocumentKind;

export type ClientDialogContext = DocumentKind | 'sale-pattern' | 'other-buyer';

interface ClientFormDialogViewModel extends DialogContentViewModel {
    isFormSaving: boolean;
    isFormInEditMode: boolean;
    showValidations: BehaviorSubject<boolean>,
    isFormReady: boolean;
    countries: CountryItem[];

    saveInClients: boolean;
    saveInClientsDisabled: boolean;

    buyerVisible: boolean;
    supplierContext: boolean;

    dialogTitle: string;
    isAdjustmentMode: boolean;
    buyer: { value: Partial<ClientQLCollectionItem> | null; } | null;
    previousBuyer: { value: Partial<ClientQLCollectionItem> | null } | null;
}

export interface ClientFormDialogData {
    readonly localContext: LocalContext;
    readonly context: ClientDialogContext;
    readonly previewMode?: boolean;

    readonly previousClientModel?: DocumentClientModel | null | undefined;
    readonly previousBuyerModel?: DocumentBuyerModel | null | undefined;

    readonly clientModel?: DocumentClientModel | null | undefined;
    readonly buyerModel?: DocumentBuyerModel | null | undefined;

    readonly clientId?: number | null;
    readonly clientNameOrTin?: string | null;

    readonly goToClientsMessages?: {
        buyer: string;
        client: string;
    }
}

export interface ClientFormDialogResult {
    clientModel: ClientModel;
    buyerModel?: ClientModel | null;
}

@Component({
    selector: 'skl-client-dialog',
    templateUrl: './client-dialog.component.html',
    styleUrl: './client-dialog.component.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    encapsulation: ViewEncapsulation.None,
    imports: [ClientDialogFormComponent, ClientDialogOtherBuyerFormComponent, DialogContentComponentIns, DialogHeaderComponentIns, FormContainerComponent,  FormsModule,  ButtonComponent, TranslatePipe, BooleanValueAccessorDirective, ValidationAreaComponent]
})
export class ClientDialogComponent extends DialogContentComponentBase<ClientFormDialogViewModel, ClientFormDialogData> {
    bo!: ClientBO;
    previousBo!: ClientBO;

    buyerBo!: ClientBO | null;
    previousBuyerBo!: ClientBO | null;

    commonAreaId = 'commonAreaId';
    otherBuyerAreaId = 'otherBuyerAreaId';

    #queryBus = inject(QueryBus);
    #routerService = inject(RoutingService);
    #clientRepository = inject(ClientRepository) as ClientRepositoryBase;
    #l10n = inject(L10n) as L10nBase;

    protected override getInitialState(): ClientFormDialogViewModel {
        return {
            ...super.getInitialState(),
            isFormInEditMode: !!this.data.clientId || !!this.data.clientModel,
            showValidations: new BehaviorSubject(false),
            isFormReady: false,
            buyer: null,
            countries: [],
            saveInClients: false,
            saveInClientsDisabled: (this.data.context === 'other-buyer') || (this.data.context === 'sale-pattern') || !!this.data.previewMode,
            buyerVisible: ![PurchaseDocumentKind.Expense, 'other-buyer'].includes(this.data.context),
            supplierContext: (<ClientDialogContext[]>[
                PurchaseDocumentKind.Expense, PurchaseDocumentKind.PurchaseInvoice, PurchaseDocumentKind.PurchaseInvoiceAdjustment,
                InventoryDocumentKind.GoodsReceipt, InventoryDocumentKind.GoodsReceiptAdjustment, InventoryDocumentKind.InternalGoodsReceipt
            ]).includes(this.data.context),
            isFormSaving: false,
        };
    }

    private loadOrCreateBoAsync(model: DocumentClientModel | DocumentBuyerModel): Promise<ClientBO> {
        return model.sourceClientId
            ? this.#clientRepository.loadAsync(model.sourceClientId, this.data.localContext)
            : this.#clientRepository.createNewAsync(this.data.localContext);
    }

    private async prepareBOAsync(): Promise<void> {
        if (this.data.clientModel) {
            this.bo = await this.loadOrCreateBoAsync(this.data.clientModel);
            fillClientBasedOnDocumentClient(this.bo.model, this.data.clientModel);

            if (this.data.buyerModel) {
                this.buyerBo = await this.loadOrCreateBoAsync(this.data.buyerModel);
                fillClientBasedOnDocumentClient(this.buyerBo.model, this.data.buyerModel);
                this.bo.model.buyerId = this.data.buyerModel.sourceClientId;
                this.bo.model.buyerRole = this.data.buyerModel.role || null;
            }

            if (this.data.previousClientModel) {
                this.previousBo = await this.#clientRepository.createNewAsync(this.data.localContext);
                fillClientBasedOnDocumentClient(this.previousBo.model, this.data.previousClientModel);
            }

            if (this.data.previousBuyerModel) {
                this.previousBuyerBo = await this.#clientRepository.createNewAsync(this.data.localContext);
                fillClientBasedOnDocumentClient(this.previousBuyerBo.model, this.data.previousBuyerModel);
                this.previousBo.model.buyerRole = this.data.previousBuyerModel.role || null;
            }
        } else if (this.data.clientId) {
            this.bo = await this.#clientRepository.loadAsync(this.data.clientId, this.data.localContext);
        } else {
            this.bo = await this.#clientRepository.createNewAsync(this.data.localContext);

            const validIncomingNip = NipValidator.isValid(this.data.clientNameOrTin);
            validIncomingNip
                ? this.bo.model.tin = this.data.clientNameOrTin!
                : this.bo.model.name = this.data.clientNameOrTin!;

            if (validIncomingNip) {
                runInInjectionContext(this.injector, async () => {
                    await clientSetDataFromGus(this.bo);
                })
            }

            this.bo.model.kind = ClientKind.Company;

            const defaultCountryId = this.data.localContext.firstPreloaded(CountryCollectionQuery, { default: true })?.id;
            this.bo.model.countryId = defaultCountryId;
        }
    }

    protected override async prepareDataAsync(): Promise<void> {
        await this.prepareBOAsync();
        const self = this;
        let buyer: Partial<ClientQLCollectionItem> | null = null;
        let previousBuyer: Partial<ClientQLCollectionItem> | null = null;

        if (this.buyerBo) {
            buyer = { id: this.buyerBo.model.id, name: this.buyerBo.model.name };
        }
        if (this.previousBuyerBo) {
            previousBuyer = { id: this.previousBuyerBo.model.id, name: this.previousBuyerBo.model.name };
        }

        this.viewModel.previousBuyer = { get value() {
            return previousBuyer;
        } } ;

        this.viewModel.buyer = {
            get value(): Partial<ClientQLCollectionItem> | null{
                return buyer;
            },
            set value(value: Partial<ClientQLCollectionItem> | null) {
                buyer = value;
                if (value === null) {
                    self.bo.model.buyerId = null;
                    self.buyerBo?.dispose();
                    self.buyerBo = null;
                } else {
                    self.bo.model.buyerId = value.id!;
                    (async () => {
                        if (value.id) {
                            self.buyerBo = await self.#clientRepository.loadAsync(value.id, self.data.localContext);
                        }
                    })()
                }
            }
        };

        if (this.bo.model.id || !this.viewModel.isFormInEditMode) {
            this.viewModel.saveInClients = true;
        }

        this.viewModel.showValidations.next(this.viewModel.isFormInEditMode)

        this.viewModel.countries = await this.data.localContext.preload(CountryCollectionQuery);

        this.viewModel.isAdjustmentMode = !!this.data.previousClientModel;
        this.viewModel.dialogTitle = this.#l10n._T(this.data.previewMode
            ? 'scenes.shared.dialog.client.title.preview'
            : this.viewModel.isAdjustmentMode
                ? 'scenes.shared.dialog.client.title.adjustment'
                : this.viewModel.isFormInEditMode
                    ? 'scenes.shared.dialog.client.title.edit'
                    : 'scenes.shared.dialog.client.title.new');

        this.viewModel.isFormReady = true;
    }

    public async saveAsync(): Promise<void> {
        try {
            this.viewModel.isFormSaving = true;
            this.viewModel.showValidations.next(true);

            await this.bo.waitForProcessingCompletionAsync();
            if (!this.bo.isValid) return;

            if (this.buyerBo) {
                await this.buyerBo.waitForProcessingCompletionAsync();
                if (!this.buyerBo.isValid) return;
            }

            const duplicateDialogResult = await this.getDuplicatesValidationResultAsync();
            if (duplicateDialogResult.state === DialogState.Cancel) return;

            let result: ClientFormDialogResult;

            if ((duplicateDialogResult.state === DialogState.Confirm) && duplicateDialogResult.data?.clientId) {
                const duplicated = await this.#clientRepository.findAsync(duplicateDialogResult.data.clientId);
                const duplicatedBuyer = duplicated.buyerId ? await this.#clientRepository.findAsync(duplicated.buyerId) : null;
                result = {
                    clientModel: duplicated,
                    buyerModel: duplicatedBuyer
                };
            } else {
                if (this.viewModel.saveInClients) {
                    if (this.buyerBo) {
                        const buyerSaveResponse = await this.#clientRepository.saveAsync(this.buyerBo);
                        if (buyerSaveResponse.code !== RepositoryResponseCode.Success) return;
                    }

                    const response = await this.#clientRepository.saveAsync(this.bo);
                    if (response.code !== RepositoryResponseCode.Success) return;
                }

                result = {
                    clientModel: this.bo.model,
                    buyerModel: this.buyerBo ? this.buyerBo.model : null
                };
            }

            if (!result) return;

            this.dialogService.close({
                state: DialogState.Confirm,
                data: result
            });
        } catch (err) {
            this.dialogService.close({ state: DialogState.Cancel });
            this.logger.error(err);
        } finally {
            this.viewModel.isFormSaving = false;
        }
    }

    private async getDuplicatesValidationResultAsync(): Promise<DialogResolution<SelectDuplicatedClientDialogResult>> {
        const continueResult = {
            state: DialogState.Confirm, data: { clientId: null }
        };

        if (!this.bo.model.id && this.bo.model.tin) {
            const uniqueTinQueryParam: ClientsWithSameTinQueryProperties = {
                tin: this.bo.model.tin,
                tinKind: this.bo.model.tinKind,
                omitedClientId: this.bo.model.id
            };
            const query = new ClientsWithSamePropertyQuery(uniqueTinQueryParam);
            const duplicates = await this.data.localContext.getAsync(ContextKeys.tin(this.bo.model.tin, this.bo.model.tinKind), () => this.#queryBus.queryAsync(query));
            if (duplicates?.length) {
                return await this.dialogService.showAsync<{ collection: ClientsWithSameNameItem[] }, SelectDuplicatedClientDialogResult>({
                    component: SelectDuplicatedClientDialogComponent,
                    componentProps: {
                        collection: duplicates
                    },
                });
            }
        }

        return continueResult;
    }

    public redirectToClients(id?: number): void {
        if (!id) return;

        this.dialogService.close({ state: DialogState.Cancel });
        this.#routerService.navigate([AppStates.editClient.url], {queryParams: { id }})
    }

    protected override dispose(): void {
        super.dispose();
        this.deregisterAllListeners();

        this.previousBuyerBo?.dispose();
        this.buyerBo?.dispose();

        this.previousBo?.dispose();
        this.bo?.dispose();
    }

    public override async onAction(data: DialogButtonResult): Promise<void> {
        if (data.state === DialogState.Confirm) {
            await this.saveAsync();
        } else {
            this.onCancel();
        }
    }
}
